import { useState } from 'react';
import { Button, Input, Space, Table, Tag, message } from 'antd';
import { DownloadOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { OwnerType } from '@never2busy/shared';
import { projectsApi, type ProjectListItem } from '../../../api/endpoints/projects';
import { downloadBlob } from '../../../api/downloadBlob';
import { PageHeader } from '../../../components/common/PageHeader';
import { KeywordFilter } from '../../../components/common/KeywordFilter';

export function ProjectsListPage(): JSX.Element {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [keywordIds, setKeywordIds] = useState<number[]>([]);

  const keywordIdsCsv = keywordIds.length > 0 ? keywordIds.join(',') : undefined;

  const { data, isFetching } = useQuery({
    queryKey: ['projects', { page, pageSize, search, keywordIdsCsv }],
    queryFn: () =>
      projectsApi.list({
        page,
        pageSize,
        search: search || undefined,
        sortDir: 'asc',
        keywordIds: keywordIdsCsv,
      }),
  });

  const handleExport = (): void => {
    downloadBlob('/projects/export.csv', 'proyectos.csv', {
      search: search || undefined,
      keywordIds: keywordIdsCsv,
    }).catch(() => message.error('No se pudo exportar'));
  };

  const columns: ColumnsType<ProjectListItem> = [
    { title: 'Número', dataIndex: 'projectNumber', width: 120 },
    {
      title: 'Nombre',
      dataIndex: 'name',
      render: (_, row) => <Link to={`/proyectos/${row.id}`}>{row.name}</Link>,
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      render: (v?: string | null) => (v ? <Tag>{v}</Tag> : '—'),
    },
    {
      title: 'Inicio',
      dataIndex: 'startDate',
      render: (v?: string | null) => (v ? dayjs(v).format('DD/MM/YYYY') : '—'),
    },
    {
      title: 'Fin',
      dataIndex: 'endDate',
      render: (v?: string | null) => (v ? dayjs(v).format('DD/MM/YYYY') : '—'),
    },
    {
      title: 'Monto',
      dataIndex: 'amount',
      align: 'right',
      render: (v?: string | null, row?) =>
        v ? `${parseFloat(v).toLocaleString('es-MX')} ${row?.currency?.code ?? ''}` : '—',
    },
  ];

  return (
    <>
      <PageHeader
        title="Proyectos"
        crumbs={[{ title: 'Inicio', to: '/' }, { title: 'Proyectos' }]}
        extra={
          <Space>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>
              Exportar CSV
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/proyectos/nuevo')}
            >
              Nuevo proyecto
            </Button>
          </Space>
        }
      />

      <Space style={{ marginBottom: 16 }}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Buscar por nombre o número…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ width: 320 }}
        />
        <KeywordFilter
          ownerType={OwnerType.PROJECT}
          value={keywordIds}
          onChange={(ids) => {
            setKeywordIds(ids);
            setPage(1);
          }}
        />
      </Space>

      <Table<ProjectListItem>
        scroll={{ x: 'max-content' }}
        rowKey="id"
        loading={isFetching}
        columns={columns}
        dataSource={data?.data ?? []}
        pagination={{
          current: page,
          pageSize,
          total: data?.meta.total ?? 0,
          showSizeChanger: true,
          showTotal: (total) => `${total} proyectos`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />
    </>
  );
}
